import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { Box, LinearProgress, Typography } from "@mui/material";
import { setIsOpen } from "@store/accountSetupSlice";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

const AccountSetupProgressButton: FC<{
  compact: boolean;
  onOpen?: () => void;
}> = ({ compact, onOpen }) => {
  const { progress } = useSelector((state: RootState) => state.accountSetup);
  const patient = useSelector((state: RootState) => state.home.selectedPatient);
  const dispatch = useDispatch();

  if (progress >= 100) return null;

  const onClick = () => {
    dispatch(setIsOpen(true));
    onOpen?.();
  };

  if (compact) {
    return (
      <Box
        onClick={onClick}
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5,
          px: 1, height: 28, borderRadius: "14px",
          maxWidth: 56, width: "100%", boxSizing: "border-box",
          bgcolor: "#EEF4FF", border: "1px solid #90CAF9",
          cursor: "pointer",
          "&:hover": { bgcolor: "#E3EEFF" },
        }}
      >
        <RocketLaunchOutlinedIcon sx={{ color: "primary.main", fontSize: 14, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.7rem", color: "primary.main", fontWeight: 700, lineHeight: 1 }}>
          {progress}%
        </Typography>
      </Box>
    );
  }

  const name = patient?.nickname ?? patient?.firstName;
  const label = name
    ? `Get ${name}'s account ready`
    : "Get your loved one's account ready";

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5,
        borderRadius: 3, bgcolor: "#EEF4FF", border: "1px solid #90CAF9",
        cursor: "pointer", width: "100%", boxSizing: "border-box",
        "&:hover": { bgcolor: "#E3EEFF" },
      }}
    >
      <RocketLaunchOutlinedIcon sx={{ color: "primary.main", fontSize: 20, flexShrink: 0 }} />
      <Box flex={1} minWidth={0}>
        <Typography fontSize="0.8125rem" fontWeight={600} color="primary.main" noWrap>
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ flex: 1, borderRadius: 4, height: 5, bgcolor: "rgba(25,118,210,0.15)" }}
          />
          <Typography fontSize="0.75rem" color="primary.main" fontWeight={600} sx={{ flexShrink: 0 }}>
            {progress}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AccountSetupProgressButton;
